import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'
import './UserManagement.css'

type UserItem = {
  id: string
  username: string
  email: string
  first_name?: string
  last_name?: string
  phone?: string
  is_active: boolean
  role_name?: string
  role_id?: string
}

type RoleInfo = {
  id: string
  name: string
  description?: string
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'מנהל',
  TenantAdmin: 'מנהל',
  customer: 'לקוח (משתמש רגיל)',
  pilot: 'טייס',
  technician: 'טכנאי',
  sales: 'מכירות',
}

const emptyForm = {
  username: '',
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  phone: '',
  role_name: '',
  is_active: true,
}

export default function UserManagement() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [createError, setCreateError] = useState('')

  const { data: users = [], isLoading, error } = useQuery<UserItem[]>({
    queryKey: ['users'],
    queryFn: () => api.get('/users').then((r) => r.data),
  })

  const { data: roles = [] } = useQuery<RoleInfo[]>({
    queryKey: ['roles'],
    queryFn: () => api.get('/users/roles').then((r) => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (body: typeof emptyForm) => api.post('/users', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowCreate(false)
      setForm(emptyForm)
      setCreateError('')
    },
    onError: (err: any) => {
      setCreateError(err.response?.data?.detail || 'שגיאה ביצירת משתמש')
    },
  })

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.username.trim() || !form.email.trim() || !form.password.trim()) return
    createMutation.mutate({
      ...form,
      username: form.username.trim(),
      email: form.email.trim(),
      first_name: form.first_name.trim() || undefined as any,
      last_name: form.last_name.trim() || undefined as any,
      phone: form.phone.trim() || undefined as any,
      role_name: form.role_name || undefined as any,
    })
  }

  const set = (key: keyof typeof emptyForm, value: string | boolean) =>
    setForm((f) => ({ ...f, [key]: value }))

  return (
    <div className="um-page">
      <div className="um-header">
        <div className="um-header-left">
          <Link to="/settings" className="um-back">← הגדרות</Link>
          <h1>ניהול משתמשים</h1>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          + משתמש חדש
        </button>
      </div>

      {showCreate && (
        <div className="um-form-card">
          <h3>יצירת משתמש חדש</h3>
          <form onSubmit={handleCreate} className="um-form-grid">
            <label>
              שם משתמש (חובה)
              <input
                value={form.username}
                onChange={(e) => set('username', e.target.value)}
                placeholder="username"
                required
                dir="ltr"
              />
            </label>
            <label>
              אימייל (חובה)
              <input
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="user@example.com"
                required
                dir="ltr"
              />
            </label>
            <label>
              סיסמה (חובה)
              <input
                type="password"
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                placeholder="סיסמה"
                required
              />
            </label>
            <label>
              שם פרטי
              <input
                value={form.first_name}
                onChange={(e) => set('first_name', e.target.value)}
                placeholder="שם פרטי"
              />
            </label>
            <label>
              שם משפחה
              <input
                value={form.last_name}
                onChange={(e) => set('last_name', e.target.value)}
                placeholder="שם משפחה"
              />
            </label>
            <label>
              טלפון
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="טלפון (אופציונלי)"
              />
            </label>
            <label>
              תפקיד
              <select
                value={form.role_name}
                onChange={(e) => set('role_name', e.target.value)}
              >
                <option value="">— ללא תפקיד —</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.name}>
                    {ROLE_LABELS[r.name] ?? r.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="um-checkbox-label">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => set('is_active', e.target.checked)}
              />
              פעיל
            </label>
            {createError && <p className="form-error um-form-full">{createError}</p>}
            <div className="form-actions um-form-full">
              <button
                type="button"
                onClick={() => { setShowCreate(false); setCreateError('') }}
              >
                ביטול
              </button>
              <button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'שומר...' : 'צור משתמש'}
              </button>
            </div>
          </form>
        </div>
      )}

      {isLoading && <p className="um-loading">טוען משתמשים...</p>}
      {error && <p className="um-error">שגיאה בטעינת נתונים</p>}

      {!isLoading && !error && (
        <div className="um-table-wrap">
          {users.length === 0 ? (
            <p className="um-empty">אין משתמשים. צור משתמש חדש.</p>
          ) : (
            <table className="um-table">
              <thead>
                <tr>
                  <th>שם מלא</th>
                  <th>שם משתמש</th>
                  <th>אימייל</th>
                  <th>תפקיד</th>
                  <th>סטטוס</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td dir="ltr">{u.username}</td>
                    <td dir="ltr">{u.email}</td>
                    <td>
                      {u.role_name ? (
                        <span className={`role-badge role-${u.role_name}`}>
                          {ROLE_LABELS[u.role_name] ?? u.role_name}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      <span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}>
                        {u.is_active ? 'פעיל' : 'לא פעיל'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="um-action-btn"
                        onClick={() => navigate(`/settings/user-management/${u.id}`)}
                      >
                        עריכה / צפייה
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}
